import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import { StoreElement as SE, Store } from '../../stateManagement';
import { TransitionService, StateDeclaration } from '@uirouter/angularjs';

import { Roles } from '../../common/';
import { IRootScope } from '../../IRootScope';
import { Group, ScriptTestMode, Interval, ComponentDefinition, ConnectionPointType, Dashboard, IdentifierType, RefreshClickData, IApplicationConfiguration, IFrequency, IStringDateRange, ValidationScope, IStringDateRange2, Template, AcquisitionStrategy, IReferenceStateForm } from '../../models';
import { IIntervalService, INavigationHistoryService, ICurrentApplicationService, IAuthorizationService, IStoreService, IGroupEnforceAlignService, IDateHelperFormatService, IDateRangeService, IRelativeGroupHelperService, IDataReferenceState } from '../../services';
import { refreshClick, isEditMode, momentFormat } from '../../common';
import { NavigationMode } from './navigationMode';

interface IGroupScope extends angular.IScope {
    vm: GroupStateController;
}

interface IGroupState {
    selectedGroup: Group;
    title: string;
}

class GroupStateController implements IGroupState {
    allGroups: Group[];
    appConfiguration: IApplicationConfiguration;
    currentBeginDate: string;
    currentEndDate: string;
    currentInterval: IStringDateRange;
    currentState: string;
    dashboards: Dashboard[];
    data: IDataReferenceState;
    disabledModes: ScriptTestMode[];
    format: string;
    formController: IReferenceStateForm;
    groupCanNavigate = true;
    groupHasValidation = false;
    hasApprobation: boolean;
    initialDate: string;
    initialRange: IStringDateRange2;
    interval: Interval;
    isCodeEditorDisabled: boolean;
    isEditMode: boolean;
    isEditable: boolean;
    isLoading: boolean;
    isPlaying: boolean;
    launchRange: IStringDateRange2;
    manualGroups: Group[];
    manualSpotGroups: Group[];
    navigationMode: NavigationMode = NavigationMode.Double;
    referenceGroup: string;
    scriptTestModes = ScriptTestMode;
    selectedGroup: Group;
    selectedScriptTestMode: ScriptTestMode;
    share: any;
    startAutoRefresh: boolean = false;
    store: Store;
    templates: Template[];
    title: string;
    validationPagesMapping: { [name: string]: string };

    readonly dates: IStringDateRange2;
    readonly group: Group;

    private _appFrequency: IFrequency;
    private _cleanup: Function[] = [];
    private _navigationSingle = ['validation'];
    private _onlyBeginEndMode = ['reconciliation'];

    // current group is resolved using the resolve of the route.
    static $inject = [
        '$rootScope',
        '$scope',
        '$state',
        '$transitions',
        '$translate',
        'storeService',
        'intervalService',
        'navigationHistoryService',
        'currentApplicationService',
        'authorizationService',
        'groupEnforceAlignService',
        'dateHelperFormatService',
        'dateRangeService',
        'relativeGroupHelperService'
    ];

    constructor(
        private _$rootScope: IRootScope,
        private _$scope: IGroupScope,
        private _$state: angular.ui.IStateService,
        private _$transitions: TransitionService,
        private _$translate: angular.translate.ITranslateService,
        private _storeService: IStoreService,
        private _intervalService: IIntervalService,
        private _navigationHistoryService: INavigationHistoryService,
        private _currentApplicationService: ICurrentApplicationService,
        private _authService: IAuthorizationService,
        private _alignService: IGroupEnforceAlignService,
        private _dateHelperFormatService: IDateHelperFormatService,
        private _dateRangeService: IDateRangeService,
        private _relativeGroupHelperService: IRelativeGroupHelperService
    ) {
        this.format = this._dateHelperFormatService.getDateFormat();
    }

    $onInit() {
        this._dateRangeService.initByState(this._$state.params);
        this.initialRange = {
            beginDate: this._dateRangeService.beginDate() as string,
            endDate: this._dateRangeService.endDate() as string
        };
        this.isEditable = this._authService.canEdit();
        this.share = {};
        this.appConfiguration = this._currentApplicationService.getApplicationConfiguration();
        this._appFrequency = {
            interval: this.appConfiguration.interval,
            intervalTimeUnit: this.appConfiguration.intervalTimeUnit
        };
        this.hasApprobation = (this.appConfiguration) ? this.appConfiguration.validationEnabled : false;

        //Needed for sub-pages, breaks everything
        this.selectedGroup = this.group;
        if (this.group) {
            this.title = `${this.group.uniqueName} (${this.group.type}) - ${this.group.shortComment}`;
        }

        const t = this._$transitions.onStart({}, transition => this.setState(transition.to()));
        this._cleanup.push(t);

        this.updateHistoryName(this.group.uniqueName);

        this._$scope.$on(isEditMode, (_e, data) => {
            this.isEditMode = data;
        });

        this._$scope.$on(refreshClick, (_event, data: RefreshClickData) => {
            const beginMoment = moment(data.beginDate);
            this.initialDate = beginMoment.format(momentFormat);
            if (!data.isLogbookEntryUpdated) {
                this.currentInterval = { startDate: data.beginDate, endDate: data.endDate };
                this.launchRange = { beginDate: data.beginDate, endDate: data.endDate };
            }
        });

        this._$scope.$watchGroup(
            [() => this.currentBeginDate, () => this.currentEndDate],
            () => {
                // initial range must exist, otherwise don't update it since it will trigger refresh
                if (this.initialRange && this.currentBeginDate && this.currentEndDate) {
                    // this happens when a initial range was passed by URL
                    // but the date picker decided to change theses dates in our back
                    // /!\ this method should NOT be called lightly, it has a special group alignment previous/next
                    // in this cases it's ok as we only reset the initialDates variable
                    this.enforceInitialDates({ beginDate: this.currentBeginDate, endDate: this.currentEndDate });
                }
            }
        );

        this._$scope.$on('$destroy', () => this.$onDestroy());

        this.isEditMode = this._$rootScope.isEditMode;
        this.groupCanNavigate = this._authService.isInRoles([Roles.master, Roles.engineer]);
        this.setState(this._$state.current);
        this.interval = this._intervalService.getInterval(this.group.frequency.interval > 0
            ? this.group.frequency
            : this._appFrequency
        );

        this._cleanup.push(
            this._storeService.register(
                store => this.refreshStore(store),
                [
                    SE.ConnectionPoints,
                    SE.Dashboards,
                    SE.DigitalSets,
                    SE.Groups,
                    SE.Tags,
                    SE.Templates,
                    SE.ValidationPageConfigurations,
                    SE.ValueViewModels
                ]
            )
        );
    }

    $onDestroy() {
        _.forEach(this._cleanup, clean => clean());
    }

    private refreshStore(store: Store) {
        this.isLoading = true;
        const { connectionPoints, groups, tags, valueViewModels } = store;
        const allGroups = _.values<Group>(groups.dictionary);
        const dashboards = _.values<Dashboard>(store.dashboards.dictionary);

        const manualConnectionPointGuids = _.values(connectionPoints.dictionary)
            .filter(cp => cp.type === ConnectionPointType.Manual)
            .map(cp => cp.guid);
        const manualTags = _.values(tags.dictionary)
            .filter(t => manualConnectionPointGuids.indexOf(t.acquisitionPointGuid) >= 0);
        const filtered = _.flatten(manualTags
            .map(t => valueViewModels.dictionary[t.guid])
            .filter(vm => !!vm)
            .map(vm => vm.groupList));
        const groupsWithManual = _.uniq(filtered.map(f => f.guid))
            .map(guid => groups.dictionary[guid])
            .filter(g => !!g);
        const groupsWithSpot = _.uniq([].concat(...manualTags.filter(t => t.acquisitionStrategy === AcquisitionStrategy.Spot)
            .map(t => valueViewModels.dictionary[t.guid].groupList))
            .map(g => groups.dictionary[g.guid])
            .filter(d => !!d)
        );
        this.manualGroups = groupsWithManual;
        this.manualSpotGroups = groupsWithSpot;
        this.allGroups = allGroups;
        this.dashboards = dashboards;
        if (!groups.isLoading && this.selectedGroup) {
            this.selectedGroup = Object.assign({}, groups.dictionary[this._$state.params['groupGuid']], { parent: null, children: null });
        }
        if (this.dates?.beginDate && this.dates?.endDate) {
            this.enforceInitialDates(this.dates);
        }
        this.groupHasValidation = _.some(
            this.selectedGroup.identifierCollection,
            i => i.type === IdentifierType.Validated
        );

        const validationPages = store.validationPageConfigurations.dictionary;
        const validationPagesMapping = {};
        _.values(validationPages).forEach(d => validationPagesMapping[d.name] = d.referenceGroup);
        this.validationPagesMapping = validationPagesMapping;
        this.referenceGroup = this._relativeGroupHelperService.getReferenceGroup(this.group, validationPages);
        this.templates = _.values(store.templates.dictionary);
        this.store = store;
        this.isLoading = false;
    }

    private updateHistoryName(name: string) {
        if (this._$state.current.data) {
            const state = this._$translate.instant(this._$state.current.data.displayName);
            this._navigationHistoryService.updateLastHistory(`${state} - ${name}`, 'group.');
        }
    }

    private enforceInitialDates(initialRange: IStringDateRange2) {
        const isSinglePeriod = this.isSingleDate(this._$state.current);
        const range = this._alignService.groupEnforceAlign(
            {
                startDate: moment(initialRange.beginDate),
                endDate: moment(initialRange.endDate)
            },
            this.selectedGroup.frequency,
            this.appConfiguration,
            isSinglePeriod
        );
        const begin = range.startDate.toISOString();
        const end = range.endDate.toISOString();

        // also only update&refresh only if it has changed
        if (this.initialRange?.beginDate !== begin || this.initialRange?.endDate !== end) {
            this.initialRange = { beginDate: begin, endDate: end };
        }
        if (this.initialDate !== this.initialRange.beginDate) {
            this.initialDate = this.initialRange.beginDate;
        }
    }

    private setState(state: StateDeclaration | angular.ui.IState) {
        this.currentState = state.name;
        const split = state.name.split('.');
        const subStateName = split.length > 1 ? split[1] : '';
        this.navigationMode = this.isSingleDate(state)
            ? NavigationMode.Single
            : NavigationMode.Double;
        this.disabledModes = this._onlyBeginEndMode.indexOf(subStateName) >= 0
            ? [ScriptTestMode.MixedValue, ScriptTestMode.DefaultValue]
            : [];
    }

    private isSingleDate(state: StateDeclaration | angular.ui.IState):boolean{
        const split = state.name.split('.');
        const subStateName = split.length > 1 ? split[1] : '';
        const allowSingleNav = this._navigationSingle.indexOf(subStateName) >= 0;
        const isGroupSingleNav = this.group.validationScope !== ValidationScope.MultiplePeriod;
        return allowSingleNav && isGroupSingleNav;
    }
}

export const wtGroupState: ComponentDefinition = {
    componentId: 'wtGroupState',
    controller: GroupStateController,
    template: require('./groupState.component.html'),
    controllerAs: 'vm',
    bindings: {
        group: '<'
    }
};